import { useEffect, useState } from 'react';
import { UnauthorizedError, api, type Inbox, type User } from './api.js';
import { Sidebar, type NavCounts, type Tab } from './components/Sidebar.js';
import { Callbacks } from './pages/Callbacks.js';
import { Catalog } from './pages/Catalog.js';
import { Conversations } from './pages/Conversations.js';
import { Dashboard } from './pages/Dashboard.js';
import { Inboxes } from './pages/Inboxes.js';
import { Install } from './pages/Install.js';
import { Login } from './pages/Login.js';
import { Visitors } from './pages/Visitors.js';
import { faInt } from './labels.js';

const COUNTS_MS = 30_000;

const EMPTY_COUNTS: NavCounts = { online: 0, waiting: 0, pendingReview: 0 };

const TITLE_FA: Record<Tab, string> = {
  dashboard: 'امروز',
  visitors: 'آنلاین',
  conversations: 'گفتگوها',
  callbacks: 'درخواست تماس',
  catalog: 'کاتالوگ',
  inboxes: 'فروشگاه‌ها',
  install: 'نصب ویجت',
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '؟';
  if (parts.length === 1) return parts[0]!.slice(0, 2);
  return `${parts[0]![0]}.${parts[1]![0]}`;
}

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState<Tab>('dashboard');
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [activeInbox, setActiveInbox] = useState<string>('');
  const [counts, setCounts] = useState<NavCounts>(EMPTY_COUNTS);
  const [drawer, setDrawer] = useState(false);
  /** A conversation the callback list asked to open. */
  const [focus, setFocus] = useState<{ id: string; at: number } | null>(null);

  async function loadInboxes() {
    try {
      const { inboxes: rows } = await api.inboxes();
      setInboxes(rows);
      if (rows.length > 0 && !activeInbox) setActiveInbox(rows[0]!.id);
    } catch {
      /* the session check below decides what to show */
    }
  }

  /*
   * The sidebar badges come from the dashboard snapshot rather than from three
   * endpoints of their own. They are the numbers that decide where an operator
   * looks next, so they have to keep moving on every screen — not only on the
   * one that happens to be polling.
   */
  async function loadCounts() {
    if (!activeInbox) return;
    try {
      const snap = await api.dashboard(activeInbox);
      setCounts({
        online: snap.operator.onlineNow,
        waiting: snap.operator.waitingNow,
        pendingReview: snap.catalog.pending,
      });
    } catch {
      /* a stale badge is better than a page that falls over */
    }
  }

  useEffect(() => {
    void (async () => {
      try {
        const { user: me } = await api.me();
        setUser(me);
        await loadInboxes();
      } catch (err) {
        if (!(err instanceof UnauthorizedError)) console.error(err);
      } finally {
        setChecking(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user || !activeInbox) return;
    void loadCounts();
    const t = setInterval(() => void loadCounts(), COUNTS_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeInbox]);

  if (checking) {
    return (
      <div className="p-10 text-center text-sm text-ink/40">در حال بررسی نشست…</div>
    );
  }

  if (!user) {
    return (
      <Login
        onSignedIn={(u) => {
          setUser(u);
          void loadInboxes();
        }}
      />
    );
  }

  const inboxId = activeInbox || undefined;
  const box = inboxes.find((i) => i.id === activeInbox);
  const slug = box?.slug ?? 'lafoura';

  const nav = (
    <Sidebar
      tab={tab}
      onTab={(t) => {
        setTab(t);
        setDrawer(false);
      }}
      inboxes={inboxes}
      activeInbox={activeInbox}
      onInbox={setActiveInbox}
      counts={counts}
      user={user}
      onSignOut={async () => {
        await api.logout();
        setUser(null);
      }}
    />
  );

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-white md:flex-row">
      {/*
        Mobile: the nav is a drawer that slides in from the right, because this
        is RTL and the thumb that opens it is on that side. It is mounted only
        while open so its polling badges and its select do not sit behind the
        page when nobody can see them.
      */}
      <header className="flex shrink-0 items-center gap-3 border-b border-ink/[.08] bg-white px-4 py-3 md:hidden">
        <button
          onClick={() => setDrawer(true)}
          aria-label="منو"
          className="flex h-[34px] w-[34px] flex-col items-center justify-center gap-[3px] rounded-lg bg-paper-mute"
        >
          <span className="h-0.5 w-4 rounded-full bg-ink" />
          <span className="h-0.5 w-4 rounded-full bg-ink" />
          <span className="h-0.5 w-4 rounded-full bg-ink" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-md font-bold">{TITLE_FA[tab]}</div>
          <div className="truncate text-2xs text-ink/45">{box?.name ?? '—'}</div>
        </div>
        {tab === 'conversations' && counts.waiting > 0 && (
          <span className="rounded-full bg-brand px-2.5 py-1 text-2xs font-bold text-white">
            {faInt(counts.waiting)}
          </span>
        )}
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-2xs font-bold text-brand">
          {initials(user.displayName ?? user.email)}
        </span>
      </header>

      {drawer && (
        <div className="fixed inset-0 z-40 md:hidden">
          <button
            aria-label="بستن منو"
            className="absolute inset-0 bg-black/35"
            onClick={() => setDrawer(false)}
          />
          <div className="absolute inset-y-0 end-0 shadow-drawer">{nav}</div>
        </div>
      )}

      <div className="hidden md:flex md:h-full">{nav}</div>

      <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {/* Redrawn screens own their whole frame: they are full-height column
            layouts with their own scroll regions, and a wrapper that padded
            and scrolled them would give the page two scrollbars. */}
        {tab === 'dashboard' && (
          <Dashboard
            inboxId={inboxId}
            onGoto={setTab}
            onSynced={() => void loadInboxes()}
          />
        )}
        {tab === 'conversations' && (
          <Conversations inboxId={inboxId} focus={focus ?? undefined} />
        )}
        {tab === 'catalog' && <Catalog inboxId={inboxId} />}

        {/* Not redrawn yet — kept in a scrolling, padded box until they are. */}
        {(tab === 'callbacks' ||
          tab === 'visitors' ||
          tab === 'inboxes' ||
          tab === 'install') && (
          <div className="flex-1 overflow-y-auto bg-paper-app p-4 md:p-6">
            {tab === 'callbacks' && (
              <Callbacks
                inboxId={inboxId}
                onOpenConversation={(sessionId) => {
                  setFocus({ id: sessionId, at: Date.now() });
                  setTab('conversations');
                }}
              />
            )}
            {tab === 'visitors' && <Visitors inboxId={inboxId} />}
            {tab === 'inboxes' && <Inboxes onChanged={() => void loadInboxes()} />}
            {tab === 'install' && <Install slug={slug} />}
          </div>
        )}
      </main>
    </div>
  );
}
